/**
 * User Dashboard — workspace scoped to the signed-in user (not platform-wide).
 *
 * Shows: my files, my audit activity, my access requests, notifications, shared files.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import {
  getUserStats, listAllFiles, createAccessRequest,
  getNotifications, markAllNotificationsRead,
  getPendingAccessCount, getMyApprovedFiles,
  requestOTP, verifyOTP, downloadApprovedFile, getApprovedFileMetadata,
  logClientDecryptComplete,
  getRecentActivity,
  getAccessRequests,
} from '../services/api';
import { decryptFile } from '../services/encryption';
import { formatRelative } from '../utils/time';
import { DashboardCard } from '../components/user-dashboard/DashboardCard';
import { MetricCard } from '../components/user-dashboard/MetricCard';
import { ActivityItem } from '../components/user-dashboard/ActivityItem';
import { AccessRequestItem } from '../components/user-dashboard/AccessRequestItem';
import { FileCard } from '../components/user-dashboard/FileCard';

/* ─── Session cache helpers ─── */
const CACHE_KEY = 'ud_cache_v1';
const NOTIF_READ_TRACK_KEY = 'cdeaas_notif_read_at';
const NOTIF_HIDE_AFTER_READ_MS = 5 * 60 * 1000;
function readCache() {
  try { const raw = sessionStorage.getItem(CACHE_KEY); return raw ? JSON.parse(raw) : null; } catch { return null; }
}
function writeCache(data) {
  try { sessionStorage.setItem(CACHE_KEY, JSON.stringify({ ...data, _ts: Date.now() })); } catch {}
}
function loadNotifReadMap() {
  try { const raw = localStorage.getItem(NOTIF_READ_TRACK_KEY); return raw ? JSON.parse(raw) : {}; } catch { return {}; }
}
function saveNotifReadMap(map) {
  try { localStorage.setItem(NOTIF_READ_TRACK_KEY, JSON.stringify(map)); } catch {}
}

/* ─── Arrow icon for pill buttons ─── */
const arrowIcon = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14" /><path d="m12 5 7 7-7 7" />
  </svg>
);

/* ─── Skeleton shimmer block ─── */
function Skeleton({ className = '' }) {
  return <div className={`ud-skeleton rounded-2xl bg-gray-200/70 dark:bg-slate-700/50 ${className}`} />;
}

function SkeletonRow() {
  return (
    <div className="flex items-center gap-3 px-3 py-3">
      <Skeleton className="h-9 w-9 shrink-0 !rounded-xl" />
      <div className="min-w-0 flex-1 space-y-2">
        <Skeleton className="h-3.5 w-3/5 !rounded-lg" />
        <Skeleton className="h-3 w-2/5 !rounded-lg" />
      </div>
      <Skeleton className="h-3 w-16 shrink-0 !rounded-lg" />
    </div>
  );
}

function SkeletonMetric() {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white/80 p-5 backdrop-blur-sm dark:border-slate-700 dark:bg-slate-800/60">
      <div className="mb-3 flex items-center justify-between">
        <Skeleton className="h-3 w-20 !rounded-lg" />
        <Skeleton className="h-10 w-10 !rounded-xl" />
      </div>
      <Skeleton className="h-8 w-16 !rounded-lg" />
      <Skeleton className="mt-2 h-2.5 w-24 !rounded-lg" />
    </div>
  );
}

/* ─── Section header with pill action button ─── */
function SectionHeader({ title, subtitle, actionLabel, onAction, count }) {
  return (
    <div className="ud-section-header mb-5 flex items-end justify-between gap-4">
      <div>
        <h3 className="m-0 text-lg font-bold tracking-tight text-gray-900 dark:text-slate-100">
          {title}
          {count > 0 && (
            <span className="ml-2 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-indigo-100 px-1.5 text-[11px] font-bold text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-300">
              {count}
            </span>
          )}
        </h3>
        {subtitle && (
          <p className="m-0 mt-0.5 text-[12px] font-medium text-gray-500 dark:text-slate-400">{subtitle}</p>
        )}
      </div>
      {actionLabel && onAction && (
        <button type="button" onClick={onAction} className="ud-pill-btn">
          {actionLabel} {arrowIcon}
        </button>
      )}
    </div>
  );
}

/* ─── Notification icon ─── */
function NotifRowIcon({ type }) {
  const base = 'flex h-9 w-9 shrink-0 items-center justify-center rounded-xl transition-all duration-200';
  if (type === 'ACCESS_REVOKED') {
    return (<div className={`${base} bg-red-50 text-red-500 dark:bg-red-500/10 dark:text-red-400`} aria-hidden><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="4.93" y1="4.93" x2="19.07" y2="19.07" /></svg></div>);
  }
  if (type === 'ACCESS_EXTENDED') {
    return (<div className={`${base} bg-blue-50 text-blue-600 dark:bg-blue-500/10 dark:text-blue-400`} aria-hidden><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg></div>);
  }
  if (type === 'ACCESS_APPROVED' || type === 'ACCESS_REJECTED' || type === 'ACCESS_REQUESTED') {
    return (<div className={`${base} bg-amber-50 text-amber-600 dark:bg-amber-500/10 dark:text-amber-400`} aria-hidden><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg></div>);
  }
  if (type === 'FILE_SHARED' || type === 'FILE_UPLOADED') {
    return (<div className={`${base} bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-400`} aria-hidden><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg></div>);
  }
  if (type === 'SECURITY' || type === 'SECURITY_ALERT') {
    return (<div className={`${base} bg-red-50 text-red-500 dark:bg-red-500/10 dark:text-red-400`} aria-hidden><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg></div>);
  }
  return (<div className={`${base} bg-indigo-50 text-indigo-600 dark:bg-indigo-500/10 dark:text-indigo-400`} aria-hidden><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" /></svg></div>);
}

/* ─── SVG icons ─── */
const udIconFile = <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>;
const udIconDec = <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>;
const udIconClock = <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>;
const udIconShield = <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>;
const udIconInbox = <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="22 12 16 12 14 15 10 15 8 12 2 12" /><path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" /></svg>;
const udIconFolder = <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" /></svg>;
const udIconCheck = <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 6 9 17l-5-5" /></svg>;
const udIconActivity = <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" /></svg>;
const udIconBell = <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" /></svg>;

/* ─── Helpers ─── */
const ACTIVITY_LABELS = {
  LOGIN_SUCCESS: 'Signed in', LOGIN_FAIL: 'Sign-in issue',
  FILE_UPLOAD: 'File uploaded', FILE_DOWNLOAD: 'File downloaded', FILE_DELETE: 'File removed',
  OTP_SUCCESS: 'Verification succeeded', OTP_FAIL: 'Verification issue',
  OTP_VERIFIED: 'Verification succeeded', OTP_GENERATED: 'Code sent',
  ACCESS_REQUEST: 'Access requested', ACCESS_APPROVED: 'Access approved',
  ACCESS_REJECTED: 'Access declined', REGISTER: 'Account created',
};
function friendlyActivityKind(type) { if (!type) return 'Activity'; return ACTIVITY_LABELS[type] || String(type).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()); }
function accessStatusClass(status) { const s = String(status || '').toLowerCase().replace(/\s+/g, '-'); if (s.includes('pending')) return 'ud-badge ud-badge--pending'; if (s.includes('approv')) return 'ud-badge ud-badge--approved'; if (s.includes('reject') || s.includes('denied')) return 'ud-badge ud-badge--rejected'; return 'ud-badge ud-badge--neutral'; }

/* ─── Empty state (Apple-level) ─── */
function EmptyState({ icon, title, desc }) {
  return (
    <div className="ud-empty-state flex flex-col items-center rounded-2xl px-6 py-12 text-center transition-all duration-200 ease-in-out">
      <div className="ud-empty-icon mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-400 dark:bg-indigo-500/10 dark:text-indigo-400/70">
        {icon}
      </div>
      <div className="text-[13px] font-bold text-gray-900 dark:text-slate-100">{title}</div>
      <div className="mt-1 max-w-xs text-[12px] font-medium leading-relaxed text-gray-500 dark:text-slate-400">{desc}</div>
    </div>
  );
}

/* ─── Button primitive ─── */
const udBtnPrimarySm = 'rounded-lg border-0 bg-indigo-600 px-3.5 py-1.5 text-[11px] font-bold text-white shadow-sm transition-all duration-200 ease-in-out hover:bg-indigo-500 hover:shadow-md hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:ring-offset-1 dark:focus:ring-offset-slate-900 disabled:opacity-50 disabled:pointer-events-none';

/* ═══════════════════════════════════════════
   Main component
   ═══════════════════════════════════════════ */
function UserDashboard({ currentUserId, onNavigate, onDecryptRequest }) {
  /* ── Seed from cache for instant render ── */
  const cached = useMemo(() => readCache(), []);

  const [stats, setStats] = useState(cached?.stats ?? null);
  const [allFiles, setAllFiles] = useState(cached?.allFiles ?? []);
  const [filesLoading, setFilesLoading] = useState(!cached?.allFiles);
  const [notifications, setNotifications] = useState(cached?.notifications ?? []);
  const [notifReadAt, setNotifReadAt] = useState(loadNotifReadMap);
  const [notifsLoading, setNotifsLoading] = useState(!cached?.notifications);
  const [recentActivity, setRecentActivity] = useState(cached?.recentActivity ?? []);
  const [activityLoading, setActivityLoading] = useState(!cached?.recentActivity);
  const [accessRequests, setAccessRequests] = useState(cached?.accessRequests ?? []);
  const [arLoading, setArLoading] = useState(!cached?.accessRequests);
  const [pendingIn, setPendingIn] = useState(cached?.pendingIn ?? 0);
  const [pendingOut, setPendingOut] = useState(0);
  const [requestModal, setRequestModal] = useState(null);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [approvedFiles, setApprovedFiles] = useState(cached?.approvedFiles ?? []);
  const [approvedLoading, setApprovedLoading] = useState(!cached?.approvedFiles);
  const [sharedDecrypt, setSharedDecrypt] = useState(null);
  const [sdStep, setSdStep] = useState('otp');
  const [sdOtpRequested, setSdOtpRequested] = useState(false);
  const [sdOtpCode, setSdOtpCode] = useState('');
  const [sdOtpMsg, setSdOtpMsg] = useState('');
  const [sdOtpLoading, setSdOtpLoading] = useState(false);
  const [sdPassword, setSdPassword] = useState('');
  const [sdShowPw, setSdShowPw] = useState(false);
  const [sdStatus, setSdStatus] = useState('idle');
  const [sdMsg, setSdMsg] = useState('');

  /* ── Progressive loading: Wave 1 (critical) then Wave 2 (secondary) ── */
  const loadWave1 = useCallback(async () => {
    const [statsRes, activityRes, pendingRes] = await Promise.allSettled([
      getUserStats(), getRecentActivity(), getPendingAccessCount(),
    ]);
    const s = statsRes.status === 'fulfilled' ? statsRes.value : { total_files: 0, total_encryptions: 0, total_decryptions: 0, total_otp_verified: 0, failed_otp_attempts: 0 };
    const a = activityRes.status === 'fulfilled' ? (activityRes.value || []) : [];
    const p = pendingRes.status === 'fulfilled' ? (pendingRes.value?.count || 0) : 0;
    setStats(s); setRecentActivity(a); setActivityLoading(false); setPendingIn(p);
    return { stats: s, recentActivity: a, pendingIn: p };
  }, []);

  const loadWave2 = useCallback(async () => {
    const [filesRes, notifsRes, arRes, approvedRes] = await Promise.allSettled([
      listAllFiles(), getNotifications(), getAccessRequests(), getMyApprovedFiles(),
    ]);
    const f = filesRes.status === 'fulfilled' ? (filesRes.value || []) : [];
    const n = notifsRes.status === 'fulfilled' ? (notifsRes.value || []) : [];
    const ar = arRes.status === 'fulfilled' ? (arRes.value || []) : [];
    const ap = approvedRes.status === 'fulfilled' ? (approvedRes.value || []) : [];
    setAllFiles(f); setFilesLoading(false);
    setNotifications(n); setNotifsLoading(false);
    setAccessRequests(ar); setArLoading(false);
    setApprovedFiles(ap); setApprovedLoading(false);
    return { allFiles: f, notifications: n, accessRequests: ar, approvedFiles: ap };
  }, []);

  useEffect(() => {
    let w1 = {};
    loadWave1().then(res => { w1 = res; })
      .then(() => loadWave2())
      .then(w2 => { writeCache({ ...w1, ...w2 }); });
  }, [loadWave1, loadWave2]);

  useEffect(() => { setPendingOut(allFiles.filter(f => !f.is_owner && f.access_status === 'PENDING').length); }, [allFiles]);

  const myFiles = useMemo(() => (allFiles || []).filter(f => f.is_owner), [allFiles]);
  const accessIncoming = useMemo(() => (!currentUserId || !accessRequests.length) ? [] : accessRequests.filter(r => r.owner_id === currentUserId), [accessRequests, currentUserId]);
  const accessOutgoing = useMemo(() => (!currentUserId || !accessRequests.length) ? [] : accessRequests.filter(r => r.requester_id === currentUserId), [accessRequests, currentUserId]);

  /* ── handlers (untouched logic) ── */
  const handleRequestAccess = async () => {
    if (!requestModal) return; setSubmitting(true);
    try { await createAccessRequest(requestModal.id, reason); setFeedback({ type: 'success', msg: `Access request sent to ${requestModal.owner_email}` }); setRequestModal(null); setReason(''); loadWave2(); loadWave1(); }
    catch (err) { setFeedback({ type: 'error', msg: err.message || 'Failed to send request' }); }
    finally { setSubmitting(false); setTimeout(() => setFeedback(null), 4000); }
  };
  const handleMarkAllRead = async () => {
    try {
      const unreadIds = notifications.filter(n => !n.is_read).map(n => n.id);
      await markAllNotificationsRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      const readAt = Date.now();
      setNotifReadAt(prev => {
        const next = { ...prev };
        unreadIds.forEach((id) => { next[id] = readAt; });
        saveNotifReadMap(next);
        return next;
      });
    } catch {}
  };
  const openSharedDecrypt = (file) => { setSharedDecrypt(file); setSdStep('otp'); setSdOtpRequested(false); setSdOtpCode(''); setSdOtpMsg(''); setSdOtpLoading(false); setSdPassword(''); setSdShowPw(false); setSdStatus('idle'); setSdMsg(''); };
  const handleSdRequestOtp = async () => { if (!sharedDecrypt) return; setSdOtpLoading(true); setSdOtpMsg(''); try { const res = await requestOTP(sharedDecrypt.file_id); setSdOtpRequested(true); setSdOtpMsg(res.message || 'OTP sent to your email.'); } catch (err) { setSdOtpMsg(err.message || 'Failed to send OTP.'); } finally { setSdOtpLoading(false); } };
  const handleSdVerifyOtp = async () => { if (!sharedDecrypt || !sdOtpCode) return; setSdOtpLoading(true); try { await verifyOTP(sharedDecrypt.file_id, sdOtpCode); setSdStep('password'); setSdOtpMsg(''); } catch (err) { setSdOtpMsg(err.message || 'Invalid OTP. Try again.'); } finally { setSdOtpLoading(false); } };
  const handleSdDecrypt = async () => {
    if (!sharedDecrypt || !sdPassword) return; setSdStatus('decrypting'); setSdMsg('');
    try {
      const meta = await getApprovedFileMetadata(sharedDecrypt.file_id);
      const encryptedBuffer = await downloadApprovedFile(sharedDecrypt.file_id);
      const decryptedData = await decryptFile(encryptedBuffer, sdPassword, meta.iv, meta.salt, 256, meta.pbkdf2_iterations || 100000);
      const url = URL.createObjectURL(new Blob([decryptedData]));
      const a = document.createElement('a'); a.href = url; a.download = sharedDecrypt.file_name || 'decrypted-file';
      document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
      setSdStatus('success'); setSdMsg('File decrypted and downloaded successfully.');
      try { await logClientDecryptComplete(sharedDecrypt.file_id, 'shared'); } catch {}
    } catch (err) { setSdStatus('error'); setSdMsg(err.message || 'Decryption failed. Check your password.'); }
  };

  const formatSize = (bytes) => { if (!bytes) return '0 B'; if (bytes < 1024) return bytes + ' B'; if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB'; return (bytes / 1048576).toFixed(2) + ' MB'; };
  const visibleNotifications = useMemo(() => {
    const now = Date.now();
    return notifications.filter((n) => {
      if (!n.is_read) return true;
      const readAt = notifReadAt[n.id];
      if (!readAt) return true;
      return (now - readAt) < NOTIF_HIDE_AFTER_READ_MS;
    });
  }, [notifications, notifReadAt]);
  const unreadNotifs = visibleNotifications.filter(n => !n.is_read).length;
  const getCountdown = (expiryStr) => { if (!expiryStr) return null; const diff = new Date(expiryStr) - new Date(); if (diff <= 0) return { label: 'Expired', expired: true }; const hrs = Math.floor(diff / 3600000); const mins = Math.floor((diff % 3600000) / 60000); return hrs > 0 ? { label: `${hrs}h ${mins}m`, expired: false } : { label: `${mins}m`, expired: false }; };

  const failedOtp = stats?.failed_otp_attempts ?? 0;
  let riskLevel = 'low';
  if (failedOtp > 5) riskLevel = 'high';
  else if (failedOtp > 0) riskLevel = 'medium';
  const statsLoading = stats === null;
  useEffect(() => {
    const onNotifRead = (event) => {
      const id = event?.detail?.id;
      const readAt = event?.detail?.readAt || Date.now();
      if (!id) return;
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
      setNotifReadAt(prev => {
        const next = { ...prev, [id]: readAt };
        saveNotifReadMap(next);
        return next;
      });
    };
    const onNotifReadAll = (event) => {
      const ids = event?.detail?.ids || [];
      const readAt = event?.detail?.readAt || Date.now();
      if (!ids.length) return;
      setNotifications(prev => prev.map(n => ids.includes(n.id) ? { ...n, is_read: true } : n));
      setNotifReadAt(prev => {
        const next = { ...prev };
        ids.forEach((id) => { next[id] = readAt; });
        saveNotifReadMap(next);
        return next;
      });
    };
    const onNotifClearAll = () => {
      setNotifications([]);
      setNotifReadAt({});
      saveNotifReadMap({});
    };
    window.addEventListener('notif:read', onNotifRead);
    window.addEventListener('notif:read-all', onNotifReadAll);
    window.addEventListener('notif:clear-all', onNotifClearAll);
    return () => {
      window.removeEventListener('notif:read', onNotifRead);
      window.removeEventListener('notif:read-all', onNotifReadAll);
      window.removeEventListener('notif:clear-all', onNotifClearAll);
    };
  }, []);

  /* ═══ RENDER ═══ */
  return (
    <div
      className="admin-page-shell user-workspace-shell user-dashboard-premium user-dashboard bg-gradient-to-b from-gray-50 via-white to-gray-50/50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950/80"
      style={st.shell}
    >
      {/* ── Page greeting ── */}
      <div className="admin-dash-fade-up" style={{ marginBottom: 28 }}>
        <div className="flex items-center gap-3">
          <div className="ud-greeting-dot flex h-2.5 w-2.5 rounded-full bg-emerald-500" style={{ boxShadow: '0 0 8px rgba(16,185,129,0.5)' }} />
          <span className="text-[12px] font-semibold text-gray-500 dark:text-slate-400">
            {unreadNotifs > 0
              ? `${unreadNotifs} unread notification${unreadNotifs > 1 ? 's' : ''}`
              : 'All systems normal'}
          </span>
        </div>
      </div>

      {/* ═══ SECTION 1: Metrics ═══ */}
      <div className="admin-dash-fade-up admin-dash-delay-1 mb-8">
        {statsLoading ? (
          <div className="ud-skel-stagger grid gap-4 grid-cols-1 sm:grid-cols-2 xl:grid-cols-4">{[0,1,2,3].map(i => <SkeletonMetric key={i} />)}</div>
        ) : (
          <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 xl:grid-cols-4">
            <MetricCard icon={udIconFile} value={stats?.total_files ?? 0} label="Total Files" footer={stats?.total_files > 0 ? 'In your vault' : null} accent="blue" />
            <MetricCard icon={udIconDec} value={stats?.total_decryptions ?? 0} label="Downloads" footer={stats?.total_decryptions > 0 ? 'This month' : null} accent="emerald" />
            <MetricCard icon={udIconClock} value={`${pendingIn + pendingOut}`} label="Access Requests" footer={pendingIn + pendingOut > 0 ? 'Pending review' : null} accent="amber" />
            <MetricCard icon={udIconShield} value="Active" label="Security Score" footer="OTP protection active" accent="indigo" />
          </div>
        )}
      </div>

      {/* ═══ SECTION 2: Activity + Quick Summary ═══ */}
      <div className="admin-dash-fade-up admin-dash-delay-2 mb-8 grid gap-5 grid-cols-1 xl:grid-cols-[minmax(0,1fr)_340px]">
        <DashboardCard>
          <SectionHeader title="My Activity" subtitle="Your recent actions" />
          {activityLoading ? (
            <div className="ud-skel-stagger">{[0,1,2,3,4].map(i => <SkeletonRow key={i} />)}</div>
          ) : recentActivity.length === 0 ? (
            <EmptyState icon={udIconCheck} title="All caught up" desc="No security events recorded yet for your account." />
          ) : (
            <ul className="m-0 list-none p-0">
              {recentActivity.map((ev) => (
                <ActivityItem
                  key={`${ev.event_type}-${ev.created_at}-${ev.description?.slice(0, 24)}`}
                  title={friendlyActivityKind(ev.event_type)}
                  time={ev.created_at ? formatRelative(ev.created_at) : ''}
                  description={ev.description}
                  eventType={ev.event_type}
                />
              ))}
            </ul>
          )}
        </DashboardCard>

        <DashboardCard securityLevel={riskLevel} className="self-start h-fit w-full">
          <SectionHeader title="Quick Summary" subtitle="Overview of your workspace" />
          <div className="flex flex-col">
            <div className={`ud-summary-row flex items-center gap-3 rounded-lg px-2 py-3.5 transition-colors duration-200 hover:bg-gray-50 dark:hover:bg-slate-700/40 ${pendingIn > 0 ? 'ud-summary-row--active' : ''}`}>
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600 dark:bg-indigo-500/10 dark:text-indigo-400">{udIconActivity}</div>
              <span className="flex-1 text-[13px] font-medium text-gray-600 dark:text-slate-300">Requests for your files</span>
              <strong className="text-lg font-extrabold tabular-nums text-gray-900 dark:text-slate-100">{pendingIn}</strong>
            </div>
            <div className={`ud-summary-row flex items-center gap-3 rounded-lg px-2 py-3.5 transition-colors duration-200 hover:bg-gray-50 dark:hover:bg-slate-700/40 ${pendingOut > 0 ? 'ud-summary-row--active' : ''}`}>
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600 dark:bg-blue-500/10 dark:text-blue-400">{udIconFolder}</div>
              <span className="flex-1 text-[13px] font-medium text-gray-600 dark:text-slate-300">Your outgoing requests</span>
              <strong className="text-lg font-extrabold tabular-nums text-gray-900 dark:text-slate-100">{pendingOut}</strong>
            </div>
            <div className="ud-summary-row ud-summary-row--last flex items-center gap-3 rounded-lg px-2 py-3.5 transition-colors duration-200 hover:bg-gray-50 dark:hover:bg-slate-700/40">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-amber-50 text-amber-600 dark:bg-amber-500/10 dark:text-amber-400">{udIconBell}</div>
              <span className="flex-1 text-[13px] font-medium text-gray-600 dark:text-slate-300">Incorrect OTP tries</span>
              <strong className={`text-lg font-extrabold tabular-nums ${failedOtp > 5 ? 'text-red-500 dark:text-red-400' : 'text-gray-900 dark:text-slate-100'}`}>{failedOtp}</strong>
            </div>
          </div>
        </DashboardCard>
      </div>

      {/* ═══ SECTION 3: Access Requests ═══ */}
      <div className="admin-dash-fade-up admin-dash-delay-2 mb-8">
        <DashboardCard hoverLift={false}>
          <SectionHeader title="Access Requests" subtitle="Manage file access permissions" actionLabel="View all" onAction={() => onNavigate('access-requests')} />
          {arLoading ? (
            <div className="grid gap-6" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
              <div className="ud-skel-stagger">{[0,1,2].map(i => <SkeletonRow key={i} />)}</div>
              <div className="ud-skel-stagger">{[0,1,2].map(i => <SkeletonRow key={i} />)}</div>
            </div>
          ) : (
            <div className="grid gap-6" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
              <div>
                <div className="mb-3 text-[11px] font-extrabold uppercase tracking-widest text-gray-400 dark:text-slate-500">Incoming</div>
                {accessIncoming.length === 0 ? (
                  <EmptyState icon={udIconInbox} title="Nothing waiting" desc="Incoming requests for your files will appear here." />
                ) : (
                  <ul className="m-0 list-none p-0">{accessIncoming.slice(0, 6).map(r => (
                    <AccessRequestItem key={r.id} primaryLine={r.file_name} metaLine={`${r.requester_email}${r.created_at ? ' · ' + formatRelative(r.created_at) : ''}`} status={r.status} statusClassName={accessStatusClass(r.status)} />
                  ))}</ul>
                )}
              </div>
              <div>
                <div className="mb-3 text-[11px] font-extrabold uppercase tracking-widest text-gray-400 dark:text-slate-500">Outgoing</div>
                {accessOutgoing.length === 0 ? (
                  <EmptyState icon={udIconClock} title="No outgoing requests" desc="Requests you send to other owners will appear here." />
                ) : (
                  <ul className="m-0 list-none p-0">{accessOutgoing.slice(0, 6).map(r => (
                    <AccessRequestItem key={r.id} primaryLine={r.file_name} metaLine={`${r.owner_email}${r.created_at ? ' · ' + formatRelative(r.created_at) : ''}`} status={r.status} statusClassName={accessStatusClass(r.status)} />
                  ))}</ul>
                )}
              </div>
            </div>
          )}
        </DashboardCard>
      </div>

      {/* ═══ SECTION 4: Notifications ═══ */}
      <div className="admin-dash-fade-up admin-dash-delay-2 mb-8">
        <DashboardCard hoverLift={false}>
          <SectionHeader
            title="Notifications"
            subtitle="Stay updated on important events"
            count={unreadNotifs}
            actionLabel={unreadNotifs > 0 ? 'Mark all read' : undefined}
            onAction={unreadNotifs > 0 ? handleMarkAllRead : undefined}
          />
          <div className="user-notif-feed ud-notif-feed-surface divide-y divide-gray-100/90 dark:divide-slate-700/50">
            {notifsLoading ? (
              <div className="ud-skel-stagger">{[0,1,2,3].map(i => <SkeletonRow key={i} />)}</div>
            ) : visibleNotifications.length === 0 ? (
              <EmptyState icon={udIconInbox} title="All caught up" desc="Approvals, shares, and alerts will show here when something needs your attention." />
            ) : (
              visibleNotifications.slice(0, 12).map((n) => {
                const nTitle = n.title || 'Notification';
                const desc = n.message || '';
                const isUnread = !n.is_read;
                return (
                  <div
                    key={n.id}
                    className={`user-notif-row group flex items-start gap-3.5 px-4 py-4 transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] ${isUnread ? 'user-notif-row--unread' : ''}`}
                  >
                    <div className="relative shrink-0">
                      <NotifRowIcon type={n.type} />
                      {isUnread && (
                        <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full border-2 border-white bg-indigo-500 dark:border-slate-800" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className={`text-[13px] leading-snug ${isUnread ? 'font-bold text-gray-900 dark:text-slate-100' : 'font-medium text-gray-600 dark:text-slate-300'}`}>
                        {nTitle}
                      </div>
                      {desc && (
                        <div className="mt-0.5 text-[12px] font-medium leading-snug text-gray-500 dark:text-slate-400">{desc}</div>
                      )}
                      <div className="mt-1 text-[11px] font-semibold text-gray-400 dark:text-slate-500">
                        {n.created_at ? formatRelative(n.created_at) : ''}
                      </div>
                    </div>
                    {isUnread && (
                      <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-indigo-500" />
                    )}
                  </div>
                );
              })
            )}
          </div>
        </DashboardCard>
      </div>

      {/* ═══ SECTION 5: Shared + My Files ═══ */}
      <div className="admin-dash-fade-up admin-dash-delay-3 mb-8">
        <div className="grid gap-5 grid-cols-1 xl:grid-cols-2">
          <DashboardCard>
            <SectionHeader title="Shared with Me" subtitle="Files shared with you" actionLabel="View all" onAction={() => onNavigate && onNavigate('all-files')} />
            {approvedLoading ? (
              <div className="ud-skel-stagger">{[0,1,2].map(i => <SkeletonRow key={i} />)}</div>
            ) : approvedFiles.length === 0 ? (
              <EmptyState icon={udIconInbox} title="No shared files" desc="Files shared with you by others will appear here." />
            ) : (
              <div className="ud-shared-files-grid grid gap-3 grid-cols-1 sm:grid-cols-2">
                {approvedFiles.map((af) => {
                  const cd = getCountdown(af.expiry_time);
                  const isExpired = cd?.expired;
                  return (
                    <FileCard key={af.request_id} variant="shared" title={af.file_name} subtitle={`${af.owner_name || af.owner_email} · ${formatSize(af.file_size)}`}
                      metaRight={<>
                        <span className="ud-badge ud-badge--info">{af.encryption_algorithm}</span>
                        {isExpired ? <span className="ud-badge ud-badge--rejected">Expired</span> : <button type="button" onClick={() => openSharedDecrypt(af)} className={udBtnPrimarySm}>Decrypt</button>}
                      </>}
                    />
                  );
                })}
              </div>
            )}
          </DashboardCard>

          <DashboardCard>
            <SectionHeader title="My Files" subtitle="Your personal files" actionLabel="View all" onAction={() => onNavigate('files')} />
            {filesLoading ? (
              <div className="ud-skel-stagger">{[0,1,2,3].map(i => <SkeletonRow key={i} />)}</div>
            ) : myFiles.length === 0 ? (
              <EmptyState icon={udIconFolder} title="No encrypted files yet" desc="Upload & Encrypt a file to see it here." />
            ) : (
              <div className="ud-my-files-stack flex flex-col gap-3">
                {myFiles.slice(0, 8).map((file) => (
                  <FileCard key={file.id} variant="owned">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600 transition-transform duration-300 group-hover:scale-105 dark:bg-indigo-500/10 dark:text-indigo-400">{udIconFile}</div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[13px] font-semibold text-gray-900 dark:text-slate-100">{file.original_filename}</div>
                      <div className="mt-0.5 text-[11px] font-medium text-gray-500 dark:text-slate-400">You &middot; {formatSize(file.file_size)} &middot; {file.encryption_algorithm}</div>
                    </div>
                    <div className="shrink-0">
                      <button type="button" onClick={() => onDecryptRequest && onDecryptRequest(file.id)} className={udBtnPrimarySm}>Decrypt</button>
                    </div>
                  </FileCard>
                ))}
              </div>
            )}
          </DashboardCard>
        </div>
      </div>

      {/* ═══ SECTION 6: Security Info ═══ */}
      <div className="admin-dash-fade-up admin-dash-delay-3 mb-8">
        <DashboardCard hoverLift={false}>
          <SectionHeader title="Security Info" subtitle="Your account security status" />
          <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
            <div className="ud-security-inner group rounded-2xl border border-gray-100/90 bg-gradient-to-br from-emerald-50/50 via-white/80 to-white p-5 shadow-sm ring-1 ring-black/[0.03] transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] hover:-translate-y-0.5 hover:border-emerald-200/90 hover:shadow-md hover:ring-emerald-200/20 dark:border-slate-600/50 dark:from-emerald-500/8 dark:via-slate-800/50 dark:to-slate-800/70 dark:ring-white/[0.04] dark:hover:border-emerald-500/35 dark:hover:ring-emerald-500/10">
              <div className="mb-3 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600 transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-110 dark:bg-emerald-500/10 dark:text-emerald-400">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="m9 12 2 2 4-4"/></svg>
                </div>
                <div>
                  <h4 className="m-0 text-[14px] font-bold text-gray-900 dark:text-slate-100">Password safety</h4>
                  <p className="m-0 flex items-center gap-1.5 text-[11px] font-medium text-gray-400 dark:text-slate-500">
                    <span className="ud-status-dot" /> Protected
                  </p>
                </div>
              </div>
              <p className="m-0 text-[13px] font-medium leading-relaxed text-gray-500 dark:text-slate-400">
                Encryption keys are derived from your password and never stored. If you lose your password, files <strong className="text-gray-700 dark:text-slate-200">cannot be recovered</strong>.
              </p>
            </div>

            <div className="ud-security-inner group rounded-2xl border border-gray-100/90 bg-gradient-to-br from-indigo-50/50 via-white/80 to-white p-5 shadow-sm ring-1 ring-black/[0.03] transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] hover:-translate-y-0.5 hover:border-indigo-200/90 hover:shadow-md hover:ring-indigo-200/20 dark:border-slate-600/50 dark:from-indigo-500/8 dark:via-slate-800/50 dark:to-slate-800/70 dark:ring-white/[0.04] dark:hover:border-indigo-500/35 dark:hover:ring-indigo-500/10">
              <div className="mb-3 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600 transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-110 dark:bg-indigo-500/10 dark:text-indigo-400">
                  {udIconShield}
                </div>
                <div>
                  <h4 className="m-0 text-[14px] font-bold text-gray-900 dark:text-slate-100">Access expiry &amp; local files</h4>
                  <p className="m-0 flex items-center gap-1.5 text-[11px] font-medium text-gray-400 dark:text-slate-500">
                    <span className="ud-status-dot" /> Always active
                  </p>
                </div>
              </div>
              <p className="m-0 text-[13px] font-medium leading-relaxed text-gray-500 dark:text-slate-400">
                Approved access windows only limit <strong className="text-gray-700 dark:text-slate-200">server-side</strong> download. A plaintext file saved locally stays there — protecting it is your responsibility.
              </p>
            </div>
          </div>
        </DashboardCard>
      </div>

      {/* ═══ MODALS ═══ */}
      {requestModal && (
        <div style={st.overlay} onClick={() => setRequestModal(null)}>
          <div style={st.modal} onClick={e => e.stopPropagation()}>
            <h3 style={st.modalH}>Request File Access</h3>
            <p style={st.modalP}>The file owner will review your request. Even if approved, only the <strong>encrypted</strong> file can be downloaded.</p>
            <div style={st.modalFile}>
              <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text-primary)' }}>{requestModal.original_filename}</div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>Owner: {requestModal.owner_email} &middot; {formatSize(requestModal.file_size)}</div>
            </div>
            <label style={st.fLabel}>Reason (optional)</label>
            <textarea value={reason} onChange={e => setReason(e.target.value)} placeholder="Why do you need access?" style={st.fInput} rows={3} />
            <div style={st.modalBtns}>
              <button type="button" onClick={() => setRequestModal(null)} style={st.cancelBtn}>Cancel</button>
              <button type="button" onClick={handleRequestAccess} disabled={submitting} style={st.primaryBtn}>{submitting ? 'Sending\u2026' : 'Send Request'}</button>
            </div>
          </div>
        </div>
      )}

      {sharedDecrypt && ReactDOM.createPortal(
        <div style={st.overlay} onClick={() => setSharedDecrypt(null)}>
          <div style={st.modal} onClick={e => e.stopPropagation()}>
            <h3 style={st.modalH}>Decrypt Shared File</h3>
            <p style={st.modalP}>Step-up verification required. OTP will be sent to your email.</p>
            <div style={st.modalFile}>
              <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text-primary)' }}>{sharedDecrypt.file_name}</div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>Owner: {sharedDecrypt.owner_email} &middot; {formatSize(sharedDecrypt.file_size)}</div>
            </div>

            {sdStep === 'otp' && (
              <div>
                {!sdOtpRequested ? (
                  <div style={{ padding: '12px 0', textAlign: 'center' }}>
                    <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 14 }}>Verify your identity before decryption.</p>
                    <button type="button" onClick={handleSdRequestOtp} disabled={sdOtpLoading} style={st.primaryBtn}>
                      {sdOtpLoading ? 'Sending\u2026' : 'Send OTP to My Email'}
                    </button>
                  </div>
                ) : (
                  <div>
                    {sdOtpMsg && <div style={st.otpInfo}>{sdOtpMsg}</div>}
                    <label style={st.fLabel}>Enter 6-digit OTP</label>
                    <input type="text" value={sdOtpCode}
                      onChange={e => setSdOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      onKeyDown={e => { if (e.key === 'Enter' && sdOtpCode.length === 6) handleSdVerifyOtp(); }}
                      placeholder="000000" maxLength={6} autoFocus
                      style={{ ...st.fInput, fontFamily: 'var(--font-mono, monospace)', fontSize: 20, letterSpacing: 6, textAlign: 'center', padding: 12, height: 48 }} />
                    <div style={st.modalBtns}>
                      <button type="button" onClick={() => setSharedDecrypt(null)} style={st.cancelBtn}>Cancel</button>
                      <button type="button" onClick={handleSdVerifyOtp} disabled={sdOtpCode.length < 6 || sdOtpLoading} style={st.primaryBtn}>
                        {sdOtpLoading ? 'Verifying\u2026' : 'Verify OTP'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {sdStep === 'password' && (
              <div>
                <div style={{ marginBottom: 12, padding: '8px 12px', borderRadius: 12, fontSize: 12, fontWeight: 600, color: '#15803d', background: 'linear-gradient(135deg, rgba(16,185,129,0.08), rgba(5,150,105,0.04))', border: '1px solid rgba(16,185,129,0.15)' }}>&#10003; OTP verified. Enter encryption password.</div>
                <div style={{ marginBottom: 12, padding: '8px 12px', borderRadius: 12, fontSize: 12, lineHeight: 1.5, color: '#b45309', background: 'linear-gradient(135deg, rgba(245,158,11,0.08), rgba(245,158,11,0.03))', border: '1px solid rgba(245,158,11,0.15)' }}>After decryption, the file is saved locally like any download. Server access expiry does not remove that copy.</div>
                <label style={st.fLabel}>Encryption Password</label>
                <div style={{ position: 'relative' }}>
                  <input type={sdShowPw ? 'text' : 'password'} value={sdPassword}
                    onChange={e => setSdPassword(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && sdPassword) handleSdDecrypt(); }}
                    placeholder="Password used to encrypt this file"
                    style={{ ...st.fInput, paddingRight: 50, height: 42 }} />
                  <button type="button" onClick={() => setSdShowPw(!sdShowPw)} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'transparent', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', cursor: 'pointer' }}>{sdShowPw ? 'Hide' : 'Show'}</button>
                </div>
                {sdMsg && (
                  <div style={{ marginTop: 8, padding: '8px 12px', borderRadius: 12, fontSize: 12, fontWeight: 600, color: sdStatus === 'success' ? '#15803d' : '#dc2626', background: sdStatus === 'success' ? 'rgba(16,185,129,0.08)' : 'rgba(239,68,68,0.08)', border: `1px solid ${sdStatus === 'success' ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.15)'}` }}>{sdMsg}</div>
                )}
                <div style={st.modalBtns}>
                  <button type="button" onClick={() => setSharedDecrypt(null)} style={st.cancelBtn}>Cancel</button>
                  <button type="button" onClick={handleSdDecrypt} disabled={!sdPassword || sdStatus === 'decrypting'} style={st.primaryBtn}>
                    {sdStatus === 'decrypting' ? 'Decrypting\u2026' : 'Decrypt & Download'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}

      {feedback && (
        <div className={`fixed bottom-6 right-6 z-[999] rounded-xl px-5 py-3 text-[13px] font-semibold text-white shadow-lg transition-all duration-300 ${feedback.type === 'success' ? 'bg-green-600' : 'bg-red-500'}`}>{feedback.msg}</div>
      )}
    </div>
  );
}

const st = {
  shell: { width: '100%', margin: '0 auto', padding: '0 0 16px', background: 'transparent' },
  statsRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 },
  row1: { display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(280px, 340px)', gap: 20, alignItems: 'start' },
  row2: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 20, alignItems: 'start' },
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(15,16,40,0.5)', backdropFilter: 'blur(6px)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 },
  modal: { backgroundColor: 'var(--bg-card)', borderRadius: 24, padding: 32, width: 460, maxWidth: '92vw', boxShadow: '0 24px 80px rgba(99,102,241,0.18), 0 8px 24px rgba(0,0,0,0.08)', border: '1.5px solid rgba(99,102,241,0.1)', backdropFilter: 'blur(20px)' },
  modalH: { fontSize: 18, fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 4px', letterSpacing: '-0.01em' },
  modalP: { fontSize: 13, color: 'var(--text-muted)', margin: '0 0 18px', lineHeight: 1.5 },
  modalFile: { display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', background: 'linear-gradient(135deg, rgba(99,102,241,0.05), rgba(139,92,246,0.03))', borderRadius: 16, marginBottom: 18, border: '1px solid rgba(99,102,241,0.08)' },
  modalBtns: { display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 },
  fLabel: { fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6, display: 'block' },
  fInput: { width: '100%', padding: '12px 14px', borderRadius: 14, border: '1.5px solid rgba(99,102,241,0.12)', fontSize: 14, resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box', background: 'var(--bg-elevated)', color: 'var(--text-primary)', outline: 'none', transition: 'all 0.3s cubic-bezier(0.22,1,0.36,1)' },
  otpInfo: { fontSize: 12, color: 'var(--blue)', marginBottom: 12, padding: '8px 12px', background: 'linear-gradient(135deg, rgba(99,102,241,0.06), rgba(139,92,246,0.03))', borderRadius: 12, border: '1px solid rgba(99,102,241,0.1)' },
  cancelBtn: { padding: '10px 22px', borderRadius: 14, border: '1.5px solid rgba(99,102,241,0.12)', background: 'linear-gradient(135deg, rgba(99,102,241,0.04), rgba(139,92,246,0.02))', color: 'var(--text-secondary)', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.3s cubic-bezier(0.22,1,0.36,1)' },
  primaryBtn: { padding: '10px 22px', borderRadius: 14, border: 'none', background: 'linear-gradient(135deg, #6366F1, #8B5CF6)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 4px 14px rgba(99,102,241,0.3)', transition: 'all 0.3s cubic-bezier(0.22,1,0.36,1)' },
};

export default UserDashboard;
